import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useSeo, { ensureJsonLd } from '@/hooks/useSeo';
import { episodes } from '@/mocks/manga';

const seasons = ['全部', '第一季', '第二季'] as const;

export default function Episodes() {
  const [active, setActive] = useState<string>('全部');
  const list = active === '全部' ? episodes : episodes.filter((e) => e.season === active);

  useSeo({
    title: '泡在我家的辣妹分集剧情 泡在我家的辣妹分集剧情整理',
    description:
      '泡在我家的辣妹分集剧情整理：从淋雨的辣妹到毕业季的告白，每一话关键情节都为你整理好了，追更前先扫一眼，剧情节奏稳稳拿捏。',
    keywords: '泡在我家的辣妹分集剧情,泡在我家的辣妹剧情,泡在我家的辣妹漫画剧情',
    path: '/episodes',
  });

  useEffect(() => {
    ensureJsonLd('ld-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dramaquest2.github.io/' },
        { '@type': 'ListItem', position: 2, name: '分集剧情', item: 'https://dramaquest2.github.io/episodes' },
      ],
    });
    ensureJsonLd('ld-episodes', {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '泡在我家的辣妹分集剧情',
      description: '按话整理泡在我家的辣妹漫画分集剧情。',
      url: 'https://dramaquest2.github.io/episodes',
      hasPart: episodes.map((e) => ({
        '@type': 'Article',
        name: `第${e.id}话 ${e.title}`,
        headline: `第${e.id}话 ${e.title}`,
        description: e.summary,
      })),
    });
    return () => {
      ['ld-breadcrumb', 'ld-episodes'].forEach((id) => document.getElementById(id)?.remove());
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <header className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16 lg:px-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">分集剧情</span>
            </nav>
            <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-clapperboard-line"></i>
              分集剧情
            </span>
            <h1 className="mt-4 text-2xl leading-snug text-foreground-950 md:text-4xl">
              每话讲了什么，一目了然
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              这里是《泡在我家的辣妹》的分集剧情速览，把每一话的关键情节与看点都做了精简整理，方便你在追更或重温时快速定位。
            </p>

            <div className="mt-6 inline-flex rounded-full bg-background-50 p-1">
              {seasons.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setActive(s)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    active === s
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </header>

        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((ep) => (
                <article key={ep.id} className="flex items-start gap-4 rounded-lg bg-background-100 p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-xl text-background-50">
                    {ep.id}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-heading text-lg text-foreground-950">{ep.title}</h2>
                      <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-800">{ep.tag}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{ep.summary}</p>
                    <p className="mt-3 text-xs text-foreground-400">{ep.season} · 第 {ep.id} 话</p>
                  </div>
                </article>
              ))}
            </div>

            {list.length === 0 && (
              <p className="rounded-lg bg-background-100 py-10 text-center text-sm text-foreground-600">
                该季分集剧情正在整理中，敬请期待。
              </p>
            )}

            <aside className="mt-12 rounded-lg bg-primary-900 p-6 md:p-8">
              <h3 className="font-heading text-xl text-background-50">剧情看得不过瘾？</h3>
              <p className="mt-2 text-sm text-background-200">分集是枝叶，主线是骨架。先看整体脉络，再回看细节更清晰。</p>
              <Link
                to="/plot"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
              >
                查看剧情主线
                <i className="ri-arrow-right-line"></i>
              </Link>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}